import type { RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'

/** Minimum width for chart viewBox to prevent charts from getting too small */
export const MIN_CHART_WIDTH = 400

type UseChartContainerWidthOptions = {
  /** Minimum width to clamp to (defaults to MIN_CHART_WIDTH) */
  minWidth?: number
  /** Throttle delay in ms for resize callbacks (default: 100) */
  throttleMs?: number
}

/**
 * Hook that measures container width for responsive chart sizing.
 *
 * Inspired by ECharts autoresize pattern:
 * - Skips initial ResizeObserver callback if size hasn't changed since render
 * - Throttles subsequent resize callbacks to prevent excessive re-renders
 * - Only updates state if width actually changed
 * - Clamps width to minimum value for viewBox stability
 *
 * Works with SVG viewBox for smooth CSS scaling during animations.
 *
 * @param ref - React ref to the container element
 * @param options - Configuration options
 * @returns Current container width (clamped to minWidth) or null if not yet measured
 */
function useChartContainerWidth(
  ref: RefObject<HTMLElement | null>,
  options: UseChartContainerWidthOptions = {},
): number | null {
  const { minWidth = MIN_CHART_WIDTH, throttleMs = 100 } = options
  const [containerWidth, setContainerWidth] = useState<number | null>(null)
  const lastWidthRef = useRef<number | null>(null)
  const throttleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!ref.current) return

    // Capture initial dimensions at mount time
    const { offsetWidth: initialWidth } = ref.current
    const clampedInitialWidth = Math.max(initialWidth, minWidth)
    setContainerWidth(clampedInitialWidth)
    lastWidthRef.current = clampedInitialWidth

    let initialResizeTriggered = false

    const updateWidth = () => {
      if (!ref.current) return

      const newWidth = ref.current.offsetWidth
      const clampedNewWidth = Math.max(newWidth, minWidth)

      // Only update if width actually changed (prevents unnecessary re-renders)
      if (clampedNewWidth !== lastWidthRef.current) {
        lastWidthRef.current = clampedNewWidth
        setContainerWidth(clampedNewWidth)
      }
    }

    // Trailing-edge throttle: schedules update for end of throttle window.
    // Intermediate events are dropped, but since updateWidth() reads current
    // offsetWidth (not cached values), the final update always gets correct size.
    const throttledUpdateWidth = () => {
      if (throttleTimeoutRef.current) return // Already scheduled

      throttleTimeoutRef.current = setTimeout(() => {
        throttleTimeoutRef.current = null
        updateWidth()
      }, throttleMs)
    }

    const resizeObserver = new ResizeObserver(() => {
      if (!ref.current) return

      // Skip initial ResizeObserver callback if size hasn't changed since render
      // (ECharts pattern - avoids unnecessary initial re-render)
      if (!initialResizeTriggered) {
        initialResizeTriggered = true
        if (ref.current.offsetWidth === initialWidth) {
          return
        }
      }

      throttledUpdateWidth()
    })

    resizeObserver.observe(ref.current)

    return () => {
      resizeObserver.disconnect()
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current)
      }
    }
  }, [ref, minWidth, throttleMs])

  return containerWidth
}

export default useChartContainerWidth
